import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { MapTrifold, Warning, Clock, ArrowClockwise, ArrowSquareOut, MapPin, UsersThree, HardHat, Truck, Hourglass, BellRinging } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

function fmtMins(m) {
  if (m == null) return "—";
  const h = Math.floor(m / 60); const mm = m % 60;
  return h ? `${h}h ${mm}m on site` : `${mm}m on site`;
}
const jobPath = (o) => `/${o.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${o.entity_id}`;
const pinColour = (o) => (o.is_emergency ? "bg-red-500" : o.team === "supplier" ? "bg-green-600" : "bg-blue-500");

function TeamBadge({ o }) {
  return o.team === "supplier"
    ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-700 dark:text-green-400" data-testid="live-team-badge-supplier"><Truck size={10} /> {o.supplier_name || "Supplier"}</span>
    : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-700 dark:text-blue-400" data-testid="live-team-badge-inhouse"><HardHat size={10} /> In-house</span>;
}

const FILTERS = [
  { id: "all", label: "All" },
  { id: "in_house", label: "In-house" },
  { id: "supplier", label: "Suppliers" },
];

export default function LiveTeamMapPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ live: [], expected: [], by_team: { in_house: 0, supplier: 0 }, late: 0, late_alerts: { enabled: true, grace_minutes: 30 } });
  const canEditAlerts = ["super_admin", "admin"].includes(user?.role);

  const saveAlerts = async (patch) => {
    const next = { ...data.late_alerts, ...patch };
    try {
      const { data: saved } = await API.put("/scheduler/late-crew/settings", next);
      setData((d) => ({ ...d, late_alerts: saved }));
      toast.success(saved.enabled ? `Late alerts on — admins and supplier owners pinged ${saved.grace_minutes} min after start time` : "Late crew alerts paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save alert settings"); }
  };
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mapSrc, setMapSrc] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await API.get("/admin/operatives/live-locations");
      setData({ live: d.live || [], expected: d.expected || [], by_team: d.by_team || { in_house: 0, supplier: 0 }, late: d.late || 0, date: d.date, late_alerts: d.late_alerts || { enabled: true, grace_minutes: 30 } });
      setSelected((prev) => prev && d.live?.some((o) => o.entity_id === prev.entity_id && o.operative_id === prev.operative_id) ? prev : (d.live?.[0] || null));
      try {
        const imgRes = await API.get("/admin/operatives/live-locations/map.png", { responseType: "blob" });
        if (imgRes.status === 204 || !imgRes.data || imgRes.data.size === 0) {
          setMapSrc(null);
        } else {
          const url = URL.createObjectURL(imgRes.data);
          setMapSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        }
      } catch {
        setMapSrc(null);
      }
    } catch {
      toast.error("Could not load live locations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { load(); }, 60_000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => () => { if (mapSrc) URL.revokeObjectURL(mapSrc); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const ops = data.live;
  const visible = ops.filter((o) => filter === "all" || o.team === filter);
  const expected = data.expected.filter((o) => filter === "all" || o.team === filter);

  return (
    <div className="p-6 space-y-5" data-testid="live-team-map-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <MapTrifold className="h-7 w-7 text-emerald-600" /> Live Crew Map
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            In-house operatives and supplier engineers, one pin each. Pins drop the moment someone taps "Arrive on site" and clear on completion. Auto-refreshes every minute.
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="live-map-refresh-btn">
          <ArrowClockwise className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary + filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="live-map-filters">
        {FILTERS.map((f) => {
          const n = f.id === "all" ? ops.length : data.by_team[f.id] || 0;
          return (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`live-map-filter-${f.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === f.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
              {f.id === "in_house" && <HardHat size={12} />}{f.id === "supplier" && <Truck size={12} />}{f.id === "all" && <UsersThree size={12} />}
              {f.label} <span className={`px-1.5 rounded-full text-[10px] ${filter === f.id ? "bg-white/20" : "bg-muted"}`}>{n}</span>
            </button>
          );
        })}
        {data.late > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-500/10 px-2.5 py-1 rounded-full" data-testid="live-map-late-count">
            <Hourglass size={12} weight="fill" /> {data.late} due but not arrived
          </span>
        )}
      </div>

      {ops.length === 0 && !loading ? (
        <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-12 text-center" data-testid="live-map-empty">
          <MapPin className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-4 font-semibold text-slate-700 dark:text-slate-200">Nobody's on site right now</p>
          <p className="text-sm text-slate-500 mt-1">As soon as an in-house operative or supplier engineer hits "Arrive on site" their pin will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {mapSrc ? (
              <img src={mapSrc} alt="Live crew map" className="w-full object-cover aspect-[2/1]" data-testid="live-map-image" />
            ) : (
              <div className="aspect-[2/1] flex items-center justify-center text-sm text-slate-500">
                Map unavailable — no coordinates captured.
              </div>
            )}
            <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> In-house</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-green-600" /> Supplier</span>
              <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Emergency</span>
              <span className="ml-auto">Pin numbers match the list</span>
            </div>
            {selected && (
              <div className="p-4 border-t border-border" data-testid="live-map-selected-detail">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">{selected.operative_name} <span className="text-muted-foreground font-normal">on</span> {selected.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{selected.reference} · {selected.property_address}</p>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <TeamBadge o={selected} />
                      {selected.crew_size > 1 && <span className="text-[10px] text-muted-foreground">{selected.crew_size}-person crew</span>}
                      {selected.off_site && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700" data-testid="live-map-offsite-flag"><Warning size={10} weight="fill" /> Arrived {selected.distance_m ? `${selected.distance_m}m ` : ""}off-site</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected.is_emergency && <Badge variant="destructive" className="uppercase">Emergency</Badge>}
                    <Badge className="bg-green-100 text-green-800"><Clock className="h-3 w-3 mr-1" />{fmtMins(selected.on_site_minutes)}</Badge>
                    <Link to={jobPath(selected)}>
                      <Button size="sm" variant="outline" data-testid="live-map-open-job">
                        <ArrowSquareOut className="h-3.5 w-3.5 mr-1" /> Open job
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-semibold">Who's on site now</h2>
              <Badge variant="secondary" data-testid="live-map-onsite-count">{visible.length}</Badge>
            </div>
            <div className="divide-y divide-border max-h-[60vh] overflow-y-auto" data-testid="live-map-onsite-list">
              {visible.length === 0 && <p className="p-4 text-xs text-muted-foreground">No {filter === "supplier" ? "supplier engineers" : "in-house operatives"} on site right now.</p>}
              {visible.map((o) => {
                const idx = ops.indexOf(o) + 1;
                const active = selected?.entity_id === o.entity_id && selected?.operative_id === o.operative_id;
                return (
                  <button key={`${o.entity_id}-${o.operative_id}`} onClick={() => setSelected(o)}
                          className={`w-full text-left p-3 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${active ? "bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-500" : ""}`}
                          data-testid={`live-map-op-${o.entity_id}-${o.operative_id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-6 w-6 rounded-full ${pinColour(o)} text-white text-[10px] font-semibold flex items-center justify-center shrink-0`}>{idx}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{o.operative_name || "Unknown"}</p>
                        <p className="text-xs text-slate-500 truncate">{o.title}</p>
                      </div>
                      {o.off_site && <Warning className="h-4 w-4 text-amber-500 shrink-0" title="Arrived off-site" />}
                      {o.is_emergency && <Warning className="h-4 w-4 text-red-500 shrink-0" />}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <TeamBadge o={o} />
                      <span className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtMins(o.on_site_minutes)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Due today, not yet arrived */}
      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="live-map-expected">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Hourglass size={14} className="text-amber-600" /> Due today — not yet arrived</h2>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="live-map-late-alert-settings">
              <BellRinging size={12} className={data.late_alerts?.enabled ? "text-amber-600" : ""} />
              <input type="checkbox" checked={!!data.late_alerts?.enabled} disabled={!canEditAlerts} onChange={(e) => saveAlerts({ enabled: e.target.checked })} data-testid="live-map-late-alert-toggle" />
              Ping admins + supplier owner after
              <select value={data.late_alerts?.grace_minutes || 30} disabled={!canEditAlerts || !data.late_alerts?.enabled} onChange={(e) => saveAlerts({ grace_minutes: Number(e.target.value) })}
                className="h-6 rounded border border-input bg-background px-1 text-[11px]" data-testid="live-map-late-alert-grace">
                {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </label>
            <Badge variant="secondary" data-testid="live-map-expected-count">{expected.length}</Badge>
          </div>
        </div>
        {expected.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground" data-testid="live-map-expected-empty">Everyone due today has arrived{ops.length ? "" : " — or nothing is scheduled for today"}.</p>
        ) : (
          <ul className="divide-y divide-border max-h-[40vh] overflow-y-auto">
            {expected.map((o) => (
              <li key={`${o.entity_id}-${o.operative_id}`} className="p-3 flex items-center gap-3" data-testid={`live-map-expected-${o.entity_id}-${o.operative_id}`}>
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${o.late ? "bg-amber-500" : "bg-slate-300"}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{o.operative_name || "Unknown"} <span className="text-muted-foreground font-normal">· {o.title}</span></p>
                  <p className="text-[11px] text-muted-foreground truncate">{o.reference} · {o.property_address}</p>
                </div>
                <TeamBadge o={o} />
                {o.alerted && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 shrink-0" title="Late alert sent to admins (and the supplier owner for supplier crews)" data-testid={`live-map-alerted-${o.entity_id}-${o.operative_id}`}><BellRinging size={10} weight="fill" /> Alert sent</span>}
                <span className={`text-xs font-medium shrink-0 ${o.late ? "text-amber-700" : "text-muted-foreground"}`} data-testid="live-map-expected-time">
                  {o.is_emergency ? "Emergency" : o.start_time ? `${o.start_time}${o.late ? " · late" : ""}` : "No time set"}
                  {o.eta_time && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800 dark:text-amber-300" title={o.eta_note || "Engineer sent an ETA"} data-testid={`live-map-eta-${o.entity_id}-${o.operative_id}`}>ETA {o.eta_time}</span>}
                </span>
                <Link to={jobPath(o)} className="text-primary hover:underline text-xs shrink-0"><ArrowSquareOut size={14} /></Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
